import * as React from 'react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import throttle from 'lodash/throttle';
import axios from 'axios';
import clsx from 'clsx';
import { Badge, Grid } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

export interface OptionType {
    value: string;
    label: string;
}

interface IUserAutocompleteData {
    Id: string,
    Label: string,

    Login?: string,
    FullName?: string,
    Category?: string,
    Email?: string,
    SAPId?: string,
    Company?: string,
    RegistrationNumber?: string,
    Department?: string,

    ExecutorInIssuesCounter: number,
    AssignedInIssuesCounter: number,
    CoordinatorInIssuesCounter: number,
}

function dataMapper(item: IUserAutocompleteData): OptionType {
    if (item == null) return null;
	return {
        label: item.Label,
		value: item.Id
	};
}

function optionMapper(item: OptionType): IUserAutocompleteData  {
    if (item == null) return null;
    return {
        Label: item.label,
        Id: item.value,

        ExecutorInIssuesCounter: 0,
        AssignedInIssuesCounter: 0,
        CoordinatorInIssuesCounter: 0,
    };
}

interface IProps {
    value: OptionType,
    onChange: (value: OptionType) => void,
    className?: string,
    additional?: OptionType[],
    maxRows?: number,
    filter?: string,
    filterGroup?: string,
    error?: boolean,
    label?: string,
    helperText?: string,
    counter?: string,
}

export default function UsersAutocomplete(props: IProps)
{
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = React.useState(false);
    const [open, setOpen] = React.useState(false);
    const [options, setOptions] = React.useState<IUserAutocompleteData[]>([]);
    const [inputValue, setInputValue] = React.useState('');
    const [cache, setCache] = React.useState({});
    const [currentValue, setCurrentValue] = React.useState(() => {
        return optionMapper(props.value)
    });

    React.useEffect(() => {
        setCurrentValue(optionMapper(props.value));
    }, [props.value]);

    React.useEffect(() => {
        setCache({});
    }, [props.filterGroup]);

    const fetch = React.useMemo(
        () =>
            throttle((obj, callback) => {
                loadData(obj.input, obj.filterGroup, callback);
            }, 500),
        [],
    );

    React.useEffect(() => {
        if (!open) {
            return;
        }
        if (cache[inputValue]) {
            setOptions(cache[inputValue]);
            return;
        }
        let active = true;
        fetch({ input: inputValue, filterGroup: props.filterGroup }, (results: IUserAutocompleteData[], serachText: string) => {
            if (active) {
                setOptions(results || []);

                let newCache = { ...cache };
                newCache[serachText] = results || [];
                setCache(newCache)
            }
        });

        return () => {
            active = false;
        };
    }, [inputValue, props.filterGroup, fetch, open]);

    React.useEffect(() => {
        if (props.filterGroup) {
            setOptions([]);
        }
    }, [props.filterGroup])

    function loadData(text: string, filterGroup: string, callback: (data: IUserAutocompleteData[], serachText: string) => void) {
        setIsLoading(true);
        axios.get<IUserAutocompleteData[]>("/Users/GetUsersAutocomplete/", {
            params: {
                MaxRows: props.maxRows ?? 50,
                StartsWith: text,
                filter: props.filter,
                filterGroup: filterGroup
            }
        })
            .then((result) => {
                let results: IUserAutocompleteData[] = result.data;
                if (!text && props.additional) {
                    for (let i = 0; i < props.additional.length; i++) {
                        results.unshift(optionMapper(props.additional[i]));
                    }
                }
                setIsLoading(false);
                callback(results, text);
            });
	}

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.target.value);
    };

    return (
        <Autocomplete
            className={clsx("mi-lab-autocomplete-outlined", props.className)}

            value={currentValue}
            onChange={(event, newValue) => {
                let nv = newValue as IUserAutocompleteData;
                props.onChange(dataMapper(nv));
            }}
            
            open={open}
            onOpen={() => {
                let searchValue = props.value?.label ?? "";
                const indexEnd = searchValue.indexOf(" (");
                if (indexEnd > -1) {
                    searchValue = searchValue
                        .substring(0, indexEnd);
                }

                searchValue = searchValue
                    .replace('★', '')
                    .trim()

                setInputValue(searchValue);
                setOpen(true);
            }}
            onClose={() => {
                setOpen(false);
            }}
           
            getOptionSelected={(option, value) => option.Id === value.Id}
            getOptionLabel={option => option.Label}
            options={options}
            loading={isLoading}

            clearText={t("common.action.clear")}
            noOptionsText={t("control.autocomplete.noOptionsMessage")}
            openText={t("common.action.open")}
            closeText={t("common.action.close")}
            loadingText={t("common.loading") + "..."}
            
            renderInput={params => (
                <TextField
                    {...params}
                    label={props.label}
                    variant="outlined"
                    onChange={handleChange}
                    error={props.error}
                    helperText={props.helperText}
                    fullWidth
                    InputProps={{
                        ...params.InputProps,
                        autoComplete: 'disabled',
                        endAdornment: (
                            <React.Fragment>
                                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                />
            )}

            renderOption={(option) => {
                let counter: number = option[props.counter];
                return (<Grid container alignItems="center">
                    <Grid item xs>
                        {option.Label}
                    </Grid>
                    {!!counter && <Grid item>
                        <Badge
                            badgeContent={counter}
                            title={t("control.autocomplete.user." + props.counter, { counter: counter })}
                        />
                    </Grid>}
                </Grid>)
            }}
        />
    );
}
